// @ts-expect-error
import buildMessage from 'ember-changeset-validations/utils/validation-errors';
import type { IntlService } from 'ember-intl';
import _merge from 'lodash/merge';
import application from 'volta/utils/application';
import { safeGet } from 'volta/utils/object-utils';

const intl = application?.instance?.lookup('service:intl') as IntlService;

function buildErrorMessage(key: string, i18nType: string, context: Record<string, any> = {}) {
  return buildMessage(i18nType, { type: i18nType, context: { description: key, ...context } });
}

interface IValidateOnCheckArgs {
  checkKeys: string[];
  checkCondition: (currentValue: any, ...values: any) => boolean;
  i18nContext?: (currentValue: any, ...values: any) => Record<string, any>;
  i18nKey?: string;
  i18nType?: string;
}

/**
 * Validates a value based on a condition that checks other values.
 * @param {Object} args - The arguments for the validation.
 * @param {string[]} args.checkKeys - The keys to check in the content object.
 * @param {function} args.checkCondition - The condition function to validate the value.
 * @param {string} [args.i18nKey] - The i18n key for the error message, will be the field name by default.
 * @param {string} [args.i18nType='invalid'] - The type of the error message.
 * @returns {function} A validation function that checks the condition and returns an error message if invalid.
 */
export default function validateOnCheck({
  checkKeys,
  checkCondition,
  i18nContext,
  i18nKey,
  i18nType = 'invalid'
}: IValidateOnCheckArgs) {
  return (key: string, newValue: any, __: any, changes: any, content: any) => {
    const valuesToCheck = _merge({}, content, changes);
    const check = checkKeys.map((k: string) => safeGet(valuesToCheck, k));

    return (
      checkCondition(newValue, ...check) ||
      buildErrorMessage(
        i18nKey ? intl?.t(i18nKey) : key,
        i18nType,
        i18nContext?.(newValue, ...check) || {}
      )
    );
  };
}
